import { apiClient } from './authApi';
import { createUserBody, updateUserBody } from '@carboncare/schemas';
import { preflight } from './schemaPreflight';
import { User, AccountType } from '../../types/auth';

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  accountType: AccountType;
  companyId: number;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  accountType?: AccountType;
  companyId?: number;
  branch?: string;
  mail?: string;
}

export interface GetUsersFilters {
  accountType?: AccountType;
  accountTypes?: AccountType[];
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetUsersResponse {
  data: User[];
  total: number;
  page: number;
  limit: number;
}

// Backend Joi schemas only accept this allowlist; the form carries some legacy
// fields (mail, accessTypeAnalytics, dataStorageKey, passKey) that aren't
// persisted, so we strip anything outside the allowlist before sending.
const ALLOWED_USER_PAYLOAD_KEYS = [
  'firstName',
  'lastName',
  'email',
  'accountType',
  'companyId',
  'branch',
  'subCompany',
  'subCustomerId',
] as const;

const pickUserPayload = <T extends Record<string, any>>(data: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const key of ALLOWED_USER_PAYLOAD_KEYS) {
    if (data[key] !== undefined) {
      (out as any)[key] = data[key];
    }
  }
  return out;
};

export const userApi = {
  // User CRUD operations
  getAll: async (filters?: GetUsersFilters): Promise<GetUsersResponse> => {
    const params: Record<string, string | number> = {};
    if (filters?.accountTypes && filters.accountTypes.length > 0) {
      params.accountTypes = filters.accountTypes.join(',');
    } else if (filters?.accountType !== undefined) {
      params.accountType = String(filters.accountType);
    }
    if (filters?.search) params.search = filters.search;
    if (filters?.page !== undefined) params.page = filters.page;
    if (filters?.limit !== undefined) params.limit = filters.limit;
    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortOrder) params.sortOrder = filters.sortOrder;

    const response = Object.keys(params).length
      ? await apiClient.get('/users', { params })
      : await apiClient.get('/users');

    // Support both paginated response { data, total, page, limit } and plain array
    const raw = response.data;
    if (Array.isArray(raw.data)) {
      return {
        data: raw.data,
        total: raw.total ?? raw.data.length,
        page: raw.page ?? (filters?.page ?? 0),
        limit: raw.limit ?? (filters?.limit ?? raw.data.length),
      };
    }
    // Fallback: treat the entire response body as the array
    const arr: User[] = Array.isArray(raw) ? raw : [];
    return { data: arr, total: arr.length, page: filters?.page ?? 0, limit: filters?.limit ?? arr.length };
  },

  getById: async (id: number): Promise<User> => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data.data;
  },

  create: async (userData: CreateUserData): Promise<any> => {
    const body = pickUserPayload(userData);
    preflight(createUserBody, body);
    const response = await apiClient.post('/users', body);
    // Backend returns { user, emailSent }
    return response.data.data;
  },

  update: async (id: number, userData: UpdateUserData): Promise<User> => {
    const body = pickUserPayload(userData);
    preflight(updateUserBody, body);
    const response = await apiClient.put(`/users/${id}`, body);
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  // User status management
  activate: async (id: number): Promise<User> => {
    const response = await apiClient.patch(`/users/${id}/activate`);
    return response.data.data;
  },

  deactivate: async (id: number): Promise<User> => {
    const response = await apiClient.patch(`/users/${id}/deactivate`);
    return response.data.data;
  },

  // Export users as CSV
  exportCsv: async (filters?: { search?: string; accountTypes?: number[] }): Promise<Blob> => {
    const params: Record<string, string> = {};
    if (filters?.search) params.search = filters.search;
    if (filters?.accountTypes && filters.accountTypes.length > 0) params.accountTypes = filters.accountTypes.join(',');
    const response = await apiClient.get('/users/export', { params, responseType: 'blob' });
    return response.data;
  },

  // Get users by company
  getByCompany: async (companyId: number): Promise<User[]> => {
    const response = await apiClient.get(`/companies/${companyId}/users`);
    return response.data.data;
  },

  // User profile management
  updateProfile: async (id: number, userData: UpdateUserData): Promise<User> => {
    const response = await apiClient.put(`/users/${id}/profile`, userData);
    return response.data.data;
  },

  changePassword: async (id: number, passwordData: { currentPassword: string, newPassword: string }): Promise<void> => {
    await apiClient.post(`/users/${id}/change-password`, passwordData);
  },

  updateNotificationPreferences: async (
    id: number,
    prefs: { emailNotification?: boolean; pushNotifications?: boolean }
  ): Promise<any> => {
    const response = await apiClient.patch(`/users/${id}/notification-preferences`, prefs);
    return response.data.data;
  },
  // User permissions
  updatePermissions: async (id: number, accountType: AccountType): Promise<User> => {
    const response = await apiClient.patch(`/users/${id}/permissions`, { accountType });
    return response.data.data;
  },

  // Admin: Reset 2FA for a user
  reset2FA: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/users/${id}/reset-2fa`);
    return response.data;
  },
};